import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { AuthService } from '../service/auth/auth.service';
import { RestService } from '../service/rest/rest.service';
import { AdminRestService } from '../service/rest/admin-rest.service';
import { QuestionDetailModalComponent } from '../components/question-detail-modal/question-detail-modal.component';
import { LoggerService } from '../service/utils/logger.service';

interface CourseOption {
  id: number;
  name: string;
}

interface SubjectOption {
  id: number;
  name: string;
  courseId: number;
}

interface UserOption {
  id: number;
  username: string;
}

interface UserCommentItem {
  questionId: number;
  questionHtml: string;
  subjectName: string;
  latestComment: string;
  commentCount: number;
  latestCommentAt: string;
}

@Component({
  selector: 'app-admin-question-comments',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, QuestionDetailModalComponent],
  templateUrl: './admin-question-comments.component.html',
  styleUrl: './admin-question-comments.component.css'
})
export class AdminQuestionCommentsComponent implements OnInit {
  sessionId = '';
  courses: CourseOption[] = [];
  subjects: SubjectOption[] = [];
  subscribedUsers: UserOption[] = [];

  selectedCourseId: number | null = null;
  selectedSubjectId: number | null = null;
  selectedUserId: number | null = null;

  isLoadingCourses = false;
  isLoadingSubjects = false;
  isLoadingUsers = false;
  isSearching = false;
  hasSearched = false;

  results: UserCommentItem[] = [];
  resultDisplayHtmlById: Record<number, string> = {};
  resultCommentHtmlById: Record<number, string> = {};
  rawById: Record<number, UserCommentItem> = {};

  currentPage = 1;
  itemsPerPage = 20;

  detailOpen = false;
  detailQuestionId: number | null = null;
  detailQuestionHtml: string | null = null;
  detailAnswersHtml: Array<string | null> = [];
  detailCorrectAnswer: string | null = null;

  constructor(
    private restService: RestService,
    private adminRest: AdminRestService,
    private authService: AuthService
  ) {}

  async ngOnInit() {
    const user = this.authService.getUser();
    this.sessionId = user?.sid || '';
    await this.loadCourses();
  }

  async loadCourses() {
    if (!this.sessionId) return;
    this.isLoadingCourses = true;
    try {
      const list = await this.adminRest.getAdminCourses(this.sessionId);
      this.courses = list || [];
    } catch (e) {
      LoggerService.error('Failed to load courses', e);
      this.courses = [];
    } finally {
      this.isLoadingCourses = false;
    }
  }

  async onCourseChange(courseIdValue: number | null) {
    this.selectedCourseId = courseIdValue ?? null;
    this.subjects = [];
    this.subscribedUsers = [];
    this.selectedSubjectId = null;
    this.selectedUserId = null;
    this.results = [];
    this.hasSearched = false;
    if (courseIdValue == null || courseIdValue === 0 || !this.sessionId) return;
    const courseId = Number(courseIdValue);
    if (!courseId) return;
    this.isLoadingSubjects = true;
    this.isLoadingUsers = true;
    try {
      const [subsRes, usersRes] = await Promise.all([
        this.adminRest.getAdminSubjects(this.sessionId, courseId),
        this.adminRest.getAdminSubscribedUsers(this.sessionId, courseId)
      ]);
      this.subjects = subsRes || [];
      this.subscribedUsers = (usersRes || []).slice().sort((a: UserOption, b: UserOption) =>
        (a.username || '').localeCompare(b.username || ''));
    } catch (e) {
      LoggerService.error('Failed to load subjects/users', e);
    } finally {
      this.isLoadingSubjects = false;
      this.isLoadingUsers = false;
    }
  }

  onSubjectChange() {
    this.results = [];
    this.hasSearched = false;
  }

  onUserChange() {
    this.results = [];
    this.hasSearched = false;
  }

  /** Search is allowed when a course is selected; user may be "All" (null). */
  get canSearch(): boolean {
    return this.selectedCourseId != null && !this.isSearching;
  }

  async onSearch() {
    if (!this.sessionId || this.selectedCourseId == null) return;
    this.isSearching = true;
    this.hasSearched = true;
    this.results = [];
    this.resultDisplayHtmlById = {};
    this.resultCommentHtmlById = {};
    this.rawById = {};
    this.currentPage = 1;
    try {
      const courseId = this.selectedCourseId ?? undefined;
      const subjectId = this.selectedSubjectId ?? undefined;
      const response = await this.adminRest.getAdminQuestionComments(
        this.sessionId,
        this.selectedUserId ?? null,
        courseId,
        subjectId
      );
      const rows = (response || []) as UserCommentItem[];
      this.results = rows;
      for (const r of rows) {
        this.rawById[r.questionId] = r;
        this.resultDisplayHtmlById[r.questionId] = this.stripHtml(r.questionHtml || '');
        this.resultCommentHtmlById[r.questionId] = this.stripHtml(r.latestComment || '');
      }
    } catch (e) {
      LoggerService.error('Admin question comments search failed', e);
    } finally {
      this.isSearching = false;
    }
  }

  private stripHtml(html: string): string {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  abbreviateText(text: string, maxLength: number): string {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  }

  getCommentDisplay(questionId: number): string {
    const comment = this.resultCommentHtmlById[questionId] || '';
    return this.abbreviateText(comment, 150);
  }

  getQuestionDisplay(questionId: number): string {
    const question = this.resultDisplayHtmlById[questionId] || '';
    return this.abbreviateText(question, 80);
  }

  openDetail(questionId: number) {
    this.detailQuestionId = questionId;
    const r = this.rawById[questionId];
    if (r) {
      this.detailQuestionHtml = r.questionHtml || null;
      this.loadQuestionDetails(questionId);
    }
    this.detailOpen = true;
  }

  async loadQuestionDetails(questionId: number) {
    try {
      if (!this.sessionId) return;
      const searchResults = await this.restService.searchQuestions(this.sessionId, {
        courseId: null,
        subjectId: null,
        question: null,
        answer: null,
        explanation: null,
        questionNumber: questionId.toString()
      });
      if (searchResults && searchResults.length > 0) {
        const q = searchResults[0];
        this.detailQuestionHtml = q.questionHtml || null;
        this.detailAnswersHtml = [q.answer1 || null, q.answer2 || null, q.answer3 || null, q.answer4 || null];
        this.detailCorrectAnswer = q.correctAnswer || null;
      }
    } catch (e) {
      LoggerService.error('Failed to load question details', e);
    }
  }

  closeDetail() {
    this.detailOpen = false;
    this.detailQuestionId = null;
    this.detailQuestionHtml = null;
    this.detailAnswersHtml = [];
    this.detailCorrectAnswer = null;
  }

  get totalPages(): number {
    return Math.ceil(this.results.length / this.itemsPerPage);
  }

  get paginatedResults(): UserCommentItem[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    return this.results.slice(start, start + this.itemsPerPage);
  }

  get pageNumbers(): number[] {
    const pages: number[] = [];
    const maxPages = Math.min(this.totalPages, 10);
    const startPage = Math.max(1, this.currentPage - 4);
    const endPage = Math.min(this.totalPages, startPage + maxPages - 1);
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  previousPage() {
    if (this.currentPage > 1) this.goToPage(this.currentPage - 1);
  }

  nextPage() {
    if (this.currentPage < this.totalPages) this.goToPage(this.currentPage + 1);
  }

  Math = Math;
}
